/**
 * StoreStatus constructor constructs the store status information with easy to use
 * apis based on the store hours passed to the constructor.
 * Eg: storeHours : {
 *   'monday' : {
 *     open: 'hh:mm',
 *     close: 'hh:mm'
 *    },
 *    'tuesday' : {
 *      open: 'hh:mm',
 *      close: 'hh:mm'
 *    }
 * }
 * Following APIs provide store status
 * 1. isClosedToday: Returns true if the store is closed today for the entire day else false
 * 2. isOpenNow: Returns true if the store is open right now else false.
 * 3. isClosedTomorrow: Returns true if the store is closed tomorrow else false
 * 4. getOpenTime: Returns store opening time for today.
 * 5. getCloseTime: Returns store closing time for today.
 * 6. isClosedRestOfWeek: returns true if the store the store is closed for the rest of the week else false.
 * 7. getNextOpenDayTime: returns the next open time and day for the week if there is one else returns object with null property values.
 */
const days = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

class StoreStatusUtils {
  constructor(storeHours, getDateCallback) {
    this.storeHours = storeHours;
    this.getDateCallback = getDateCallback;
  }

  _getTodayIndex() {
    return this._getDateInstance().getDay();
  }

  _isClosedAllDay(day) {
    return (
      this.storeHours[day].open === '0:00' &&
      this.storeHours[day].close === '0:00'
    );
  }

  /**
   * Get storeHours returns store hours as Object with open and close time
   * in milliseconds
   * @return {[type]} [description]
   */
  _getTodayStoreHoursInMiliseconds() {
    const now = this._getDateInstance();
    const today = days[this._getTodayIndex()];
    // extract hh:mm from storehours
    let storeTimings = this.storeHours[today].open.split(':');
    // date has been forwarded/rewinded to store opening hours
    const storeOpenTime = now.setHours(storeTimings[0], storeTimings[1], 0);
    // extract hh:mm from storehours
    storeTimings = this.storeHours[today].close.split(':');
    // date has been forwarded/rewinded to store closing hours
    const storeCloseTime = now.setHours(storeTimings[0], storeTimings[1], 0);
    return {
      openTime: storeOpenTime,
      closeTime: storeCloseTime,
    };
  }

  _getDateInstance() {
    return this.getDateCallback();
  }

  /*
  This can be used to see if the page visit is happening before the
  localized store's same-day opening time.
*/
  _isSameDayOpeningTimeNeeded() {
    return (
      this._getDateInstance().getTime() <
      this._getTodayStoreHoursInMiliseconds().openTime
    );
  }

  isClosedToday() {
    const today = days[this._getTodayIndex()];
    return this._isClosedAllDay(today);
  }

  isOpenNow() {
    // get current time in milliseconds
    const now = this._getDateInstance();
    // get store open and close hours in milliseconds
    const newStoreHours = this._getTodayStoreHoursInMiliseconds();
    return (
      !this.isClosedToday() &&
      now.getTime() >= newStoreHours.openTime &&
      now.getTime() <= newStoreHours.closeTime
    );
  }

  isClosedTomorrow() {
    const tomorrowIndex = (this._getTodayIndex() + 1) % days.length;
    const tomorrow = days[tomorrowIndex];

    return this._isClosedAllDay(tomorrow);
  }

  getOpenTime() {
    const today = days[this._getTodayIndex()];
    return this.storeHours[today].open;
  }

  getCloseTime() {
    const today = days[this._getTodayIndex()];
    return this.storeHours[today].close;
  }

  /*
    We can't always guarantee that the next open day will be tomorrow.

    This method will give the next opening day and its opening time.

    Example output: {
      day: 'monday',
      time: '6am',
    }

    If no day is found, i.e. the store is closed for the rest of the week
    (see the other helper method `isClosedRestOfWeek()`!), then
    the object returned by the method will just have null values:

    Example output: {
      day: null,
      time: null,
    }
  */
  getNextOpenDayTime() {
    /*
      Sometimes it's day of and the store is still closed.
      But we don't want to get tomorrow's opening time.
      We want to grab today's.
    */
    if (this._isSameDayOpeningTimeNeeded()) {
      return {
        day: days[this._getTodayIndex()],
        time: this.getOpenTime(),
      };
    }
    /*
      If you call the API on a Friday, the response looks like this:
      {
        friday: { open, close },
        saturday: { open, close },
        sunday: { open, close },
        monday: { open, close },
        tuesday: { open, close },
        wednesday: { open, close },
        thursday: { open, close },
      }

      The response contains open and close times for today and 6 subsequent days.

      We want to get today's index, and iterate w/mod until
      we reach `todayIndex` again to figure out the next 6 days' times.

      For example:
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday'

      So, if today is Friday, we'd have a todayIndex of 5.
      Valid indices for the loop are: [6, 0, 1, 2, 3, 4] (everything except the `todayIndex` of 5).
    */

    const todayIndex = this._getTodayIndex();
    const tomorrowIndex = (todayIndex + 1) % days.length;
    /*
      Start the loop with tomorrow's day index.

      Loop until we hit today's index again (we want to loop over the next 6 days, but not to count today twice).

      Increment the day with a mod operation to make sure we can loop back through earlier indices instead of going out of bounds.
      Same way we access "tomorrow" in other functions in this file.
    */
    for (
      let dayIndex = tomorrowIndex;
      dayIndex !== todayIndex;
      dayIndex = (dayIndex + 1) % days.length
    ) {
      const weekday = days[dayIndex];
      const { open, close } = this.storeHours[weekday];
      const closedAllDay = open === '0:00' && close === '0:00';

      if (!closedAllDay) {
        return {
          day: weekday,
          time: open,
        };
      }
    }

    return {
      day: null,
      time: null,
    };
  }

  /*
    Returns a boolean indicating whether in the next 6 days there
    is any day on which the store is open.
  */
  isClosedRestOfWeek() {
    // loop through all remaining days' times (6 indices)
    const todayIndex = this._getTodayIndex();
    const tomorrowIndex = (todayIndex + 1) % days.length;

    for (
      let dayIndex = tomorrowIndex;
      dayIndex !== todayIndex;
      dayIndex = (dayIndex + 1) % days.length
    ) {
      const weekday = days[dayIndex];
      const { open, close } = this.storeHours[weekday];

      const closedAllDay = open === '0:00' && close === '0:00';

      if (!closedAllDay) {
        return false;
      }
    }

    return true;
  }
}

module.exports = StoreStatusUtils;
